import {
  extractOrganizedTourDailyItinerary,
  splitParagraphIntoRichBlocks,
} from "../../../utils/extractOrganizedTourDailyItinerary";

/** @param {string} s */
function norm(s) {
  return String(s || "").replace(/\s+/g, " ").trim();
}

/**
 * טקסט שנראה כמו מסלול יומי / לו"ז — לא לרשימת "מה כלול" ולא לרנדר כ־checklist.
 */
export function isItineraryLikeText(s) {
  const t = norm(s);
  if (!t) return false;
  if (/יום\s*[־\-–]?\s*\d+/i.test(t)) return true;
  if (/היום\s+ה(ראשון|שני|שלישי|רביעי|חמישי|שישי|שבת)/i.test(t)) return true;
  if (/יום\s*מס[׳']?\s*\d+/i.test(t)) return true;
  if (/Day\s*\d+/i.test(t)) return true;
  if (/לאחר\s+ארוחת\s+הבוקר|אחרי\s+ארוחת\s+הבוקר|לאחר\s+הבוקר|בבוקר\s+נצא|בבוקר\s+השכם/i.test(t)) {
    return true;
  }
  if (/^\s*(ניסע|נבקר|נצא|נסעו|נבקרו|נמשיך|נגיע|ניטייל|נעלה|נבקר\s+ב|סיור\s+ב|ביקור\s+ב)(?=\s|$|[,.])/i.test(t)) {
    return true;
  }
  if (/^transfer\b/i.test(t) || /^flight\b/i.test(t)) return true;
  if (/(העברה\s+(לעיר|למלון|משדה)|שדה\s+התעופה)/i.test(t) && t.length > 35) return true;
  if (/(בוקר\s*יום|ערב\s*יום|סיום\s*יום)/i.test(t)) return true;
  const dateHits = t.match(/\d{1,2}\s*[./]\s*\d{1,2}(\s*[./]\s*\d{2,4})?/g);
  if (dateHits && dateHits.length >= 2) return true;
  if (
    t.length > 320 &&
    dateHits &&
    dateHits.length >= 1 &&
    /מלון|סיור|העברה|ארוח|נחיתה|המראה|שדה|טיסה/i.test(t)
  ) {
    return true;
  }
  return false;
}

/**
 * שורה מתאימה ל־bullet קצר ב"מה כלול" (לא פסקת מסלול).
 * @param {string} t
 */
export function isIncludedChecklistCandidateLine(t) {
  const s = String(t || "").trim();
  if (!s) return false;
  if (s.length > 220) return false;
  if (isItineraryLikeText(s)) return false;
  if (/^\s*לא\s*כולל\b|^\s*לא\s*כלול\b/i.test(s)) return false;
  if (/יום\s*\d+/i.test(s) && s.length > 35) return false;
  return true;
}

/**
 * מפרק סקשן "כולל" שספקים דוחפים בו לעיתים את כל המסלול; מחזיר פרgraphs לכלול אמיתי ומחזיר טקסט spill לפרסור מסלול.
 * @param {string[]} paragraphs
 * @returns {{ kept: string[], spill: string[] }}
 */
export function partitionIncludedParagraphs(paragraphs) {
  /** @type {string[]} */
  const kept = [];
  /** @type {string[]} */
  const spill = [];

  for (const para of paragraphs || []) {
    const p = String(para || "").trim();
    if (!p) continue;
    if (isItineraryLikeText(p)) {
      spill.push(p);
      continue;
    }
    let paraHasItin = false;
    for (const b of splitParagraphIntoRichBlocks(p)) {
      if (b.type === "p" && isItineraryLikeText(b.text)) {
        paraHasItin = true;
        break;
      }
      if (b.type === "ul" && b.items.some((ln) => isItineraryLikeText(ln))) {
        paraHasItin = true;
        break;
      }
    }
    if (paraHasItin) {
      spill.push(p);
      continue;
    }
    kept.push(p);
  }
  return { kept, spill };
}

const MAX_EXCLUDED_LINE_CHARS = 220;
const MAX_EXCLUDED_PARAGRAPH_CHARS = 520;
const MAX_EXCLUDED_SECTION_CHARS = 2800;
const MAX_EXCLUDED_BULLET_LINES = 15;

/** שורה שנחשבת כהתחלת מסלול (כולל אחרי «לא כולל») */
export function isItineraryStartLine(line) {
  const t = String(line || "").trim();
  if (!t) return false;
  if (/^יום\s*[־\-–]?\s*\d+/i.test(t)) return true;
  if (/^היום\s+ה(ראשון|שני|שלישי|רביעי|חמישי|שישי|שבת)/iu.test(t)) return true;
  if (/^יום\s*מס[׳']?\s*\d+/i.test(t)) return true;
  if (/^Day\s*\d+/i.test(t)) return true;
  if (/לאחר\s+ארוחת\s+הבוקר|אחרי\s+ארוחת\s+הבוקר|לאחר\s+הבוקר|בבוקר\s+נצא|בבוקר\s+השכם/i.test(t)) {
    return true;
  }
  if (/^\s*(ניסע|נבקר|נצא|נסעו|נבקרו|נמשיך|נגיע|ניטייל|נעלה|נבקר\s+ב|סיור\s+ב|ביקור\s+ב)(?=\s|$|[,.])/i.test(t)) {
    return true;
  }
  if (/^transfer\b/i.test(t) || /^flight\b/i.test(t)) return true;
  return false;
}

function findFirstItineraryBoundaryLineIndex(lines) {
  for (let i = 0; i < lines.length; i++) {
    const L = lines[i];
    if (!L) continue;
    if (isItineraryStartLine(L)) return i;
    if (L.length > MAX_EXCLUDED_LINE_CHARS && isItineraryLikeText(L)) return i;
    if (L.length >= MAX_EXCLUDED_PARAGRAPH_CHARS) return i;
  }
  return -1;
}

/** פיצול פסקה כש־«יום N -» מופיע באמצע טקסט אחרי רשימת לא כולל */
function splitAtEmbeddedDayInParagraph(p) {
  const re = /\s+(?=יום\s*[־\-–]?\s*\d+\s*[\-–—])/i;
  const m = re.exec(p);
  if (!m || m.index < 8) return null;
  const head = p.slice(0, m.index).trim();
  const tail = p.slice(m.index).trim();
  if (head.length < 8 || tail.length < 12) return null;
  return { head, tail };
}

function countDayMarkersInText(blob) {
  const s = String(blob || "");
  const matches = s.match(/יום\s*[־\-–]?\s*\d+|Day\s*\d+|היום\s+ה(ראשון|שני)/gi);
  return matches ? matches.length : 0;
}

function totalKeptLineCount(kept) {
  let n = 0;
  for (const p of kept || []) {
    n += String(p)
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean).length;
  }
  return n;
}

function isLikelyExcludedBulletsOnly(p) {
  const blocks = splitParagraphIntoRichBlocks(p);
  if (!blocks.length) return String(p).trim().length <= MAX_EXCLUDED_LINE_CHARS;
  for (const b of blocks) {
    if (b.type === "p") {
      if (b.text.length > MAX_EXCLUDED_PARAGRAPH_CHARS) return false;
      if (isItineraryLikeText(b.text)) return false;
    }
    if (b.type === "ul") {
      if (b.items.some((it) => it.length > MAX_EXCLUDED_LINE_CHARS || isItineraryLikeText(it))) return false;
    }
  }
  return true;
}

function applyExcludedSummaryGuards(keptIn, spillIn, trace) {
  let kept = [...keptIn];
  let spill = [...spillIn];
  const blob = kept.join("\n\n");

  if (blob.length > MAX_EXCLUDED_SECTION_CHARS) {
    trace.triggeredLongTextFallback = true;
    return { kept: [], spill: [...kept, ...spill] };
  }

  if (countDayMarkersInText(blob) > 0) {
    trace.detectedDayMarkers = true;
    return { kept: [], spill: [...kept, ...spill] };
  }

  if (totalKeptLineCount(kept) > MAX_EXCLUDED_BULLET_LINES) {
    trace.triggeredLongTextFallback = true;
    return { kept: [], spill: [...kept, ...spill] };
  }

  return { kept, spill };
}

/**
 * מפרק סקשן «לא כולל» כדי שלא יבלע מסלול — רשימה קצרה נשארת ב-excluded, השאר → spill ל־itinerary.
 * @param {string[]} paragraphs
 */
export function partitionExcludedParagraphs(paragraphs) {
  const trace = {
    detectedDayMarkers: false,
    triggeredLongTextFallback: false,
  };
  /** @type {string[]} */
  let kept = [];
  /** @type {string[]} */
  let spill = [];
  let spilling = false;

  const initialCount = (paragraphs || []).filter((x) => String(x || "").trim()).length;

  for (const para of paragraphs || []) {
    const p = String(para || "").trim();
    if (!p) continue;
    if (spilling) {
      spill.push(p);
      continue;
    }

    const embedded = splitAtEmbeddedDayInParagraph(p);
    if (embedded) {
      trace.detectedDayMarkers = true;
      const headTrim = String(embedded.head || "").trim();
      const headFirstLine = headTrim.split("\n").map((l) => l.trim()).filter(Boolean)[0] || "";
      const headIsItinerary =
        (headTrim && isItineraryLikeText(headTrim)) ||
        (headFirstLine && isItineraryStartLine(headFirstLine));
      if (headTrim && !headIsItinerary) {
        kept.push(headTrim);
      } else if (headTrim) {
        spill.push(headTrim);
      }
      spill.push(embedded.tail);
      spilling = true;
      continue;
    }

    const lines = p.split("\n").map((l) => l.trim()).filter(Boolean);
    const idx = findFirstItineraryBoundaryLineIndex(lines);

    if (idx === -1) {
      if (p.length > MAX_EXCLUDED_PARAGRAPH_CHARS && !isLikelyExcludedBulletsOnly(p)) {
        spill.push(p);
        spilling = true;
        trace.triggeredLongTextFallback = true;
      } else if (isItineraryLikeText(p)) {
        spill.push(p);
        spilling = true;
        if (countDayMarkersInText(p) > 0) trace.detectedDayMarkers = true;
      } else {
        kept.push(p);
      }
      continue;
    }

    if (lines.slice(idx).some((ln) => /יום\s*\d|Day\s*\d/i.test(ln))) trace.detectedDayMarkers = true;
    const head = lines.slice(0, idx).join("\n").trim();
    const tail = lines.slice(idx).join("\n").trim();
    if (head) kept.push(head);
    if (tail) {
      spill.push(tail);
      spilling = true;
    }
  }

  const guarded = applyExcludedSummaryGuards(kept, spill, trace);
  return {
    kept: guarded.kept,
    spill: guarded.spill,
    trace,
    initialParagraphCount: initialCount,
    finalParagraphCount: guarded.kept.length,
  };
}

function demoteMisplacedExcludedTimelineSections(items) {
  if (!items?.length) return items || [];
  return items.flatMap((it) => {
    if (it.type === "section" && String(it.key) === "excluded") {
      const paras = (it.paragraphs || []).map((p) => String(p || "").trim()).filter(Boolean);
      if (!paras.length) return [];
      return [{ type: "prose", paragraphs: paras }];
    }
    return [it];
  });
}

/**
 * מפרק timeline לטאב "תיאור" (פרוזה/Section מבוא בלבד) מול "מסלול" (ימים + טקסט מסלול).
 * @param {Array<{ type: string, paragraphs?: string[] }>} timelineItems
 */
export function splitTimelineForOfferTabs(timelineItems) {
  /** @type {typeof timelineItems} */
  const describeItems = [];
  /** @type {typeof timelineItems} */
  const itineraryItems = [];

  /** @param {typeof timelineItems[number]} it */
  const blobItineraryLike = (it) => {
    const paras = it.paragraphs || [];
    if (paras.some((p) => isItineraryLikeText(p))) return true;
    return isItineraryLikeText(paras.join("\n"));
  };

  let leading = true;
  for (const it of timelineItems || []) {
    if (!leading) {
      itineraryItems.push(it);
      continue;
    }
    if (it.type === "day") {
      leading = false;
      itineraryItems.push(it);
      continue;
    }
    if (it.type === "section") {
      if (blobItineraryLike(it)) {
        leading = false;
        itineraryItems.push(it);
      } else {
        describeItems.push(it);
      }
      continue;
    }
    if (it.type === "prose") {
      if (blobItineraryLike(it)) {
        leading = false;
        itineraryItems.push(it);
      } else {
        describeItems.push(it);
      }
      continue;
    }
    itineraryItems.push(it);
  }

  if (!itineraryItems.length && describeItems.length) {
    return { describeItems: [], itineraryItems: [...describeItems] };
  }
  return { describeItems, itineraryItems };
}

/**
 * @param {ReturnType<typeof extractOrganizedTourDailyItinerary>} parsedIn
 * @param {string} [rawTextForLen]
 */
export function refinePublicOfferParsedModel(parsedIn, rawTextForLen = "") {
  const summaryIn = parsedIn.summaryItems || [];
  /** @type {typeof summaryIn} */
  const summaryItems = [];
  /** @type {typeof parsedIn.timelineItems} */
  const prependedFromIncluded = [];
  /** @type {typeof parsedIn.timelineItems} */
  const prependedFromExcluded = [];
  let usedFallback = false;

  /** @type {null | Record<string, number | boolean>} */
  let sectionReclassifyTrace = null;

  for (const s of summaryIn) {
    if (s.type !== "section") {
      summaryItems.push(s);
      continue;
    }
    if (s.key === "included") {
      const paras = s.paragraphs || [];
      const { kept, spill } = partitionIncludedParagraphs(paras);
      if (spill.length) {
        const blob = spill.join("\n\n");
        const sub = extractOrganizedTourDailyItinerary(blob);
        usedFallback = usedFallback || !!sub.fallbackUsed;
        for (const it of sub.timelineItems || []) {
          prependedFromIncluded.push(it);
        }
      }
      if (kept.length) {
        summaryItems.push({ ...s, paragraphs: kept });
      }
      continue;
    }
    if (s.key === "excluded") {
      const paras = s.paragraphs || [];
      const part = partitionExcludedParagraphs(paras);
      sectionReclassifyTrace = {
        excludedItemsInitialCount: part.initialParagraphCount,
        excludedItemsFinalCount: part.finalParagraphCount,
        movedToItineraryCount: part.spill.length,
        detectedDayMarkers: part.trace.detectedDayMarkers,
        triggeredLongTextFallback: part.trace.triggeredLongTextFallback,
        itineraryParagraphCount: part.spill.length,
      };
      if (part.spill.length) {
        const blob = part.spill.join("\n\n");
        const sub = extractOrganizedTourDailyItinerary(blob);
        usedFallback = usedFallback || !!sub.fallbackUsed;
        for (const it of sub.timelineItems || []) {
          prependedFromExcluded.push(it);
        }
      }
      if (part.kept.length) {
        summaryItems.push({ ...s, paragraphs: part.kept });
      }
      continue;
    }
    summaryItems.push(s);
  }

  const prependedTimeline = [...prependedFromIncluded, ...prependedFromExcluded];
  let timelineItems = [...prependedTimeline, ...(parsedIn.timelineItems || [])];
  timelineItems = demoteMisplacedExcludedTimelineSections(timelineItems);

  const parsed = {
    ...parsedIn,
    summaryItems,
    timelineItems,
    daysDetected: timelineItems.filter((x) => x.type === "day").length,
    sectionsDetected: summaryItems.filter((x) => x.type === "section").length,
  };

  const modelAfter = buildTabLikeModel(parsed);
  const rawLen =
    typeof rawTextForLen === "string" ? rawTextForLen.length : String(rawTextForLen || "").length;

  if (typeof import.meta !== "undefined" && import.meta.env?.DEV) {
    // eslint-disable-next-line no-console
    console.log("[ORG_TOUR_PUBLIC_OFFER_SECTION_CLASSIFY_TRACE]", {
      hasGeneralDescription: modelAfter.describeItems.length > 0,
      itineraryTextLen: rawLen,
      includedItemsCount: modelAfter.included.length,
      excludedItemsCount: modelAfter.excluded.length,
      daysDetected: timelineItems.filter((x) => x.type === "day").length,
      usedFallback: usedFallback || !!parsed.fallbackUsed,
    });
    if (sectionReclassifyTrace) {
      // eslint-disable-next-line no-console
      console.log("[ORG_TOUR_SECTION_RECLASSIFY_TRACE]", sectionReclassifyTrace);
    }
  }

  return { parsed, classifyTrace: { usedFallback } };
}

/** מודל קלי ל־trace בלבד */
function buildTabLikeModel(parsed) {
  const { describeItems, itineraryItems } = splitTimelineForOfferTabs(parsed.timelineItems || []);
  const pick = (key) =>
    (parsed.summaryItems || []).filter((x) => x.type === "section" && x.key === key);
  return {
    describeItems,
    itineraryItems,
    included: pick("included"),
    excluded: pick("excluded"),
    notes: pick("notes"),
  };
}
